import { groupBy, labels, LocaleString, MgrLayer, MgrLayerType, selectMany, sortDates, str } from "../omnia"
import { LayerGroup, LayerGrouping } from "./types"


/**
 * Returns all groupings which can be applied to a set of layers
 *
 * @param layers Layers matching this type
 * @param scope Current scope - i.e., filters applied
 */
export function getAvailableGroupings(layers: MgrLayer[], scope: { type: MgrLayerType, year: number, cropId: number }): LayerGrouping[] {

    const { type, year, cropId } = scope

    const groupings = [

        (!year)
            ? { key: "year", name: str.Year, grouping: groupByYear }
            : undefined,

        { key: "date", name: str.Date, grouping: groupByDate },

        (!cropId)
            ? { key: "crop", name: str.Crop, grouping: groupByCrop }
            :

            layers.some(l => l.vm?.field?.fieldGroup)
                ? { key: "fieldgroup", name: str.FieldGroup, grouping: groupByFieldGroup }
                : undefined,

        { key: "field", name: str.Field, grouping: groupByField },

        { key: "layergroup", name: "Layer group" as LocaleString, grouping: groupByTypeGroup },

        (!type?.type)
            ? { key: "layertype", name: "Layer type" as LocaleString, grouping: groupByType }
            : undefined

    ].filter(g => !!g)

    return groupings

}

    /**
     * Groups a set of layers by applying one or more groupings
     * @param layers
     * @param groupings
     */
    export function groupLayers(layers: MgrLayer[], groupings: LayerGrouping[]): LayerGroup[] {

    const initialGroup: LayerGroup = {
        key: "*",
        name: "",
        layers: layers
    }

    const groups = groupings.reduce(
        (groups, grouping) => selectMany(groups.map(g => applyGrouping(grouping, g))),
        [initialGroup]
    )

    return groups

}

/**
 * Applies a single grouping to a group of layers
 * @param grouping
 * @param group
 */
function applyGrouping(grouping: LayerGrouping, group: LayerGroup): LayerGroup[] {

    const groups = grouping.grouping(group.layers)

    const result = groups.map(g => ({
        ...group,
        ...g,
        // 230819 - Usersnap #3033
        // When using multiple grouping levels, we must generate a compound key for resulting groups,
        // so that each key is unique. Using \u0000 as a separator here, as it is very unlikely to be found in actual keys.
        key: [group.key, g.key].filter(k => !!k).join('\u0000'),
        name: [group.name, g.name].filter(n => !!n).join(" - ")
    }))

    return result

}



/**
 * Group by crop
 */
function groupByCrop(layers: MgrLayer[]): LayerGroup[] {

        function getCrop(layer: MgrLayer) {
            const crop = layer.vm?.crop
            return crop
                ? { cropId: crop.CropId || 0, name: crop.Name || str.NoCrop }
            : { cropId: 0, name: layer.vm.cropping?.cropping?.CropName || str.NoCrop }
    }

    const groups = groupBy(layers, getCrop, c => c.cropId || c.name).sort((a, b) => a.key.name.localeCompare(b.key.name))

        return groups.map(g => ({
            key: g.key.name,
            name: g.key.name,
            crop: g.key,
            layers: g.values
    }))

}

/**
 * Group by layer type
 */
function groupByType(layers: MgrLayer[]): LayerGroup[] {

    const groups = groupBy(layers, l => l.vm.type, t => t.Name).sort((a, b) => a.key.Name.localeCompare(b.key.Name))

    return groups.map(g => ({
            key: g.key.Name,
            name: g.key.Name,
            layers: g.values,
            type: g.key,
    }))

}

/**
 * Group by layer type group
 */
function groupByTypeGroup(layers: MgrLayer[]): LayerGroup[] {

    const groups = groupBy(layers, l => {

        let name = labels.LayerTypeGroupId(l.vm.type.GroupId)

            if (l.vm.type.SubgroupId) {
                name += " / " + labels.LayerTypeSubgroupId(l.vm.type.SubgroupId)
            }

        return name

    })

    return groups.map(g => ({
        key: g.key,
        name: g.key,
        layers: g.values,
    }))

}

/**
 * Group by year
 */
function groupByYear(layers: MgrLayer[]): LayerGroup[] {

    const groups = groupBy(layers, l => l.year || 0).sort((a, b) => b.key - a.key)

    return groups.map(g => ({
            key: String(g.key || 0),
            name: String(g.key || str.NoYear),
            layers: g.values,
            year: g.key || 0,
    }))

}

/**
 * Group by date
 */
function groupByDate(layers: MgrLayer[]): LayerGroup[] {

    const groups = groupBy(layers, l => l.vm.date).sort((a, b) => sortDates(b.key, a.key))

    return groups.map(g => ({
            key: g.key?.toISOString() ?? "*",
            name: g.key ? g.key.toLocaleDateString() : str.NoDate,
            layers: g.values,
            date: g.key,
    }))

}

/**
 * Group by field group
 */
function groupByFieldGroup(layers: MgrLayer[]): LayerGroup[] {

    const groups = groupBy(layers, l => l.vm.field?.fieldGroup || "").sort((a, b) => a.key.localeCompare(b.key))

    return groups.map(g => ({
            key: g.key || "",
            name: g.key || str.NoFieldGroup,
            fieldGroup: g.key,
            layers: g.values
    }))

}

/**
 * Group by field
 */
function groupByField(layers: MgrLayer[]): LayerGroup[] {

    const groups = groupBy(
        layers,
        l => ({ field: l.vm.field, name: l.vm.field?.fieldName || `(${str.Holding})` }),
            i => i.field?.fieldId || 0
        ).sort((a, b) => a.key.name.localeCompare(b.key.name))

        return groups.map(g => ({
        key: String(g.key.field?.fieldId || 0),
        name: g.key.name,
        field: g.key.field,
        fieldGroup: g.key.field?.fieldGroup,
        layers: g.values
    }))

}
